/**
 * One person (homeowner account) can have several home rows.
 * Sign-in picks the current home. Archived homes stay readable.
 * No object spread: this file is required from both the app and functions.
 */

const ARCHIVED_HOME_ERROR = 'This home is archived and can only be viewed.';

const RECORD_READ_ACTIONS = {
  list: true,
  get: true,
  signed_url: true,
  page: true,
  search: true,
};

function homeIsArchived(home) {
  if (!home) return false;
  return home.status === 'archived' || !!home.archived_at;
}

function homeRank(home) {
  if (!home) return 9;
  if (home.status === 'active') return 0;
  if (home.status === 'invited') return 1;
  return 2;
}

function homeTime(home) {
  const stamp = Date.parse((home && (home.updated_at || home.created_at)) || '');
  return Number.isNaN(stamp) ? 0 : stamp;
}

/**
 * The home a sign-in should open. Current homes win over archived ones.
 * Among those, active beats invited, then the most recently updated.
 */
function pickCurrentHome(homes) {
  const rows = Array.isArray(homes) ? homes.filter(Boolean) : [];
  if (!rows.length) return null;
  const current = rows.filter((home) => !homeIsArchived(home));
  const pool = current.length ? current : rows;
  let best = pool[0];
  for (let index = 1; index < pool.length; index += 1) {
    const home = pool[index];
    const rank = homeRank(home) - homeRank(best);
    if (rank < 0 || (rank === 0 && homeTime(home) > homeTime(best))) best = home;
  }
  return best;
}

/**
 * Normalized street plus ZIP. Matches public.normalize_home_key.
 * Returns null until both a street and a 5-digit ZIP are present.
 */
function normalizeHomeKey(address, zip) {
  const street = String(address == null ? '' : address)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const digits = String(zip == null ? '' : zip).replace(/\D/g, '').slice(0, 5);
  if (!street || digits.length < 5) return null;
  return `${street}|${digits}`;
}

/** Account password wins. Older rows still keep the hash on the home. */
function loginPasswordHash(account, client) {
  if (account && account.password_hash) return account.password_hash;
  return client ? client.password_hash : null;
}

function isMissingRelation(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const message = String(error.message || '');
  return code === '42P01'
    || code === '42703'
    || code === 'PGRST204'
    || code === 'PGRST205'
    || /does not exist|schema cache|could not find the table|could not find the/i.test(message);
}

function switchHomeDecision(homes, clientId) {
  const id = String(clientId || '').trim();
  if (!id) return { status: 400, error: 'Choose a home' };
  const rows = Array.isArray(homes) ? homes : [];
  let home = null;
  for (let index = 0; index < rows.length; index += 1) {
    if (rows[index] && rows[index].id === id) home = rows[index];
  }
  if (!home) return { status: 404, error: 'Home not found' };
  return { home, readOnly: homeIsArchived(home) };
}

function recordActionBlocked(client, action) {
  if (!homeIsArchived(client)) return false;
  return !RECORD_READ_ACTIONS[action];
}

module.exports = {
  ARCHIVED_HOME_ERROR,
  homeIsArchived,
  pickCurrentHome,
  normalizeHomeKey,
  loginPasswordHash,
  isMissingRelation,
  switchHomeDecision,
  recordActionBlocked,
};
